import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <Logo size="sm" />
            </Link>
            <Link
              to="/"
              className="text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              Back
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white shadow-sm rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-sm text-gray-500 mb-8">Effective Date: November 10, 2025</p>

          <div className="prose prose-blue max-w-none">
            <p className="text-gray-700 mb-6">
              Welcome to Zest AI Marketing Content Generation Platform ("Service"). These Terms of Service ("Terms") constitute a legally binding agreement between you ("User" or "You") and Zest AI ("we", "our", or "Zest").
            </p>

            <p className="text-gray-700 mb-8">
              By using our services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our services.
            </p>

            {/* 1. Acceptance of Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-3">By accessing or using Zest AI:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>You acknowledge that you have read, understood, and agree to be bound by these Terms</li>
                <li>You represent that you are at least 18 years old or have reached the age of majority in your jurisdiction</li>
                <li>You represent that you have the authority to enter into these Terms</li>
                <li>You agree to comply with all applicable laws and regulations</li>
              </ul>
            </section>

            {/* 2. Service Description */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Service Description</h2>
              <p className="text-gray-700 mb-3">Zest AI is an AI-powered marketing content generation platform that provides:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>AI Content Generation</strong>: Generate marketing copy, social media content, blog posts, etc.</li>
                <li><strong>Content Management</strong>: Save, edit, and manage your generated content</li>
                <li><strong>Template Library</strong>: Access to preset content templates</li>
                <li><strong>Subscription Plans</strong>: Different levels of service access</li>
              </ul>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify, suspend, or discontinue any part of the Service at any time.
              </p>
            </section>

            {/* 3. User Accounts */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Account Creation</h3>
              <p className="text-gray-700 mb-3">To use our Service, you need to:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Create an account and provide accurate, complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Keep your account credentials secure</li>
                <li>Immediately notify us of any unauthorized use</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Account Responsibility</h3>
              <p className="text-gray-700 mb-4">
                You are responsible for all activities that occur under your account. You may not share your account credentials with others.
              </p>
            </section>

            {/* 4. Subscriptions and Payments */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Subscriptions and Payments</h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Subscription Plans</h3>
              <p className="text-gray-700 mb-3">We offer different subscription tiers:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Free Plan</strong>: Limited features and usage quotas</li>
                <li><strong>Paid Plans</strong>: Enhanced features and higher usage quotas</li>
                <li><strong>Enterprise Plan</strong>: Custom solutions and dedicated support</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Billing and Auto-Renewal</h3>
              <p className="text-gray-700 mb-3">
                Subscription fees are charged monthly or annually. Subscriptions will automatically renew unless you cancel.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.3 Refund Policy</h3>
              <p className="text-gray-700 mb-4">
                Subscription fees are non-refundable unless otherwise required by law. If you have billing issues, please contact us within 30 days.
              </p>
            </section>

            {/* 5. License to Use */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. License to Use</h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 License Granted</h3>
              <p className="text-gray-700 mb-4">
                We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service, provided you comply with these Terms.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Usage Restrictions</h3>
              <p className="text-gray-700 mb-3">You agree not to:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Violate any laws or regulations</li>
                <li>Infringe on the intellectual property rights of others</li>
                <li>Upload or distribute malware, viruses, or harmful code</li>
                <li>Attempt unauthorized access to our systems</li>
                <li>Use automated tools (bots, crawlers, etc.) to access the Service</li>
                <li>Use the Service for illegal or unauthorized purposes</li>
              </ul>
            </section>

            {/* 6. Content and Intellectual Property */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Content and Intellectual Property</h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Your Content</h3>
              <p className="text-gray-700 mb-4">
                You retain ownership of content you upload or generate. You grant us a license to use, store, process, and display your content to provide the Service.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 AI-Generated Content</h3>
              <p className="text-gray-700 mb-4">
                Content generated using our AI tools belongs to you. We do not claim ownership of AI-generated content.
                You are responsible for reviewing and verifying AI-generated content. We do not guarantee the accuracy, completeness, or suitability of AI-generated content.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.3 Our Intellectual Property</h3>
              <p className="text-gray-700 mb-4">
                The Service, software, brands, designs, and content (except AI-generated content) belong to Zest AI.
                You may not copy, modify, distribute, or create derivative works.
              </p>
            </section>

            {/* 7. Disclaimers */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Disclaimers</h2>
              <p className="text-gray-700 mb-3">
                The Service is provided on an "as is" and "as available" basis, without any express or implied warranties.
              </p>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-yellow-700">
                  <strong>Important:</strong> AI-generated content may contain errors or inaccuracies.
                  We do not guarantee the quality, suitability, or originality of AI content. You should review and verify all AI-generated content.
                </p>
              </div>
            </section>

            {/* 8. Limitation of Liability */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-700 mb-3">
                To the maximum extent permitted by law, our total liability shall not exceed the amount you paid to us in the 12 months preceding the claim.
              </p>
              <p className="text-gray-700 mb-4">
                We are not liable for indirect, incidental, or consequential damages, including but not limited to loss of profits, revenue, or data.
              </p>
            </section>

            {/* 9. Privacy */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Privacy and Data Protection</h2>
              <p className="text-gray-700 mb-4">
                Our Privacy Policy explains how we collect, use, and protect your information. By using the Service, you agree to our
                <Link to="/privacy" className="text-primary-600 hover:text-primary-500 mx-1">
                  Privacy Policy
                </Link>
                .
              </p>
            </section>

            {/* 10. Changes to Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We may modify these Terms at any time. We will notify you of material changes via email or service notification.
                Continued use of the Service constitutes acceptance of modified Terms.
              </p>
            </section>

            {/* 11. Contact Us */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
              <p className="text-gray-700 mb-3">
                If you have any questions, concerns, or requests about these Terms, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>Zest AI Team</strong></p>
                <p className="text-gray-700 mb-1">
                  Email:
                  <a href="mailto:reception@getaizest.com" className="text-primary-600 hover:text-primary-500 ml-1">
                    reception@getaizest.com
                  </a>
                </p>
                <p className="text-gray-700">
                  Website:
                  <a href="https://getaizest.com" className="text-primary-600 hover:text-primary-500 ml-1">
                    https://getaizest.com
                  </a>
                </p>
              </div>
            </section>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-500">Last Updated: November 10, 2025</p>
              <p className="text-sm text-gray-500">Version: 1.0</p>
              <p className="text-sm text-gray-500 mt-4">
                By using Zest AI, you acknowledge that you have read, understood, and agreed to be bound by these Terms of Service.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                © 2025 Zest AI. All rights reserved.
              </p>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <Link to="/privacy" className="text-sm text-gray-600 hover:text-gray-900 mx-4">
            Privacy Policy
          </Link>
          <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 mx-4">
            Login
          </Link>
          <Link to="/register" className="text-sm text-gray-600 hover:text-gray-900 mx-4">
            Register
          </Link>
        </div>
      </main>
    </div>
  );
};

export default Terms;
